import { ChangeEvent, DragEvent, forwardRef, useCallback, useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import styles from './FileUpload.module.css';
import { formatBytes, validateFileSize } from '@/lib/fileUpload';

interface FileUploadProps {
  name?: string;
  label?: string;
  accept?: string;
  maxSize?: number;
  value?: File | null;
  onChange?: (file: File | null) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

const DEFAULT_MAX_SIZE = 100 * 1024 * 1024;

export function isAcceptedFile(file: Pick<File, 'name' | 'type'>, accept?: string): boolean {
  if (!accept?.trim()) return true;
  const fileName = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();
  return accept.split(',').some((rawRule) => {
    const rule = rawRule.trim().toLowerCase();
    if (!rule) return false;
    if (rule.startsWith('.')) return fileName.endsWith(rule);
    if (rule.endsWith('/*')) return mimeType.startsWith(rule.slice(0, -1));
    return mimeType === rule;
  });
}

export const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      name,
      label,
      accept,
      maxSize = DEFAULT_MAX_SIZE,
      value,
      onChange,
      disabled = false,
      required = false,
      className = '',
    },
    ref,
  ) => {
    const [error, setError] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const file = value;

    const resetError = useCallback(() => setError(null), []);

    const setFile = useCallback(
      (newFile: File | null) => {
        resetError();
        onChange?.(newFile);
      },
      [onChange, resetError],
    );

    const handleFile = useCallback(
      (selectedFile: File) => {
        if (!isAcceptedFile(selectedFile, accept)) {
          setError('Choose a file in the accepted format.');
          return;
        }
        const sizeError = validateFileSize(selectedFile, maxSize);
        if (sizeError) {
          setError(sizeError);
          return;
        }
        setFile(selectedFile);
      },
      [accept, maxSize, setFile],
    );

    const onInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0] ?? null;
        if (selected) {
          handleFile(selected);
        } else {
          setFile(null);
        }
      },
      [handleFile, setFile],
    );

    const onDrop = useCallback(
      (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);
        const dropped = e.dataTransfer.files?.[0] ?? null;
        if (dropped) {
          handleFile(dropped);
        }
      },
      [handleFile],
    );

    const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(true);
    }, []);

    const onDragLeave = useCallback(() => setDragOver(false), []);

    const onRemove = useCallback(() => {
      setFile(null);
      inputRef.current && (inputRef.current.value = '');
    }, [setFile]);

    const onBrowseClick = useCallback(() => {
      inputRef.current?.click();
    }, []);

    const zoneClass = [
      styles.fileUploadZone,
      dragOver && styles.fileUploadZoneDragOver,
      disabled && styles.fileUploadZoneDisabled,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={`${styles.fileUpload}${className ? ` ${className}` : ''}`}>
        {label && (
          <label className={styles.label}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </label>
        )}

        <div
          className={zoneClass}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
        >
          <input
            ref={inputRef}
            type="file"
            name={name}
            accept={accept}
            required={required && !file}
            disabled={disabled}
            onChange={onInputChange}
            className={styles.fileUploadInput}
            tabIndex={-1}
          />

          <div className={styles.fileUploadContent}>
            {!file ? (
              <>
                <Upload size={18} className={styles.fileUploadIcon} />
                <span className={styles.fileUploadText}>
                  Drag &amp; drop or{' '}
                  <button
                    type="button"
                    className={styles.fileUploadBrowseLink}
                    disabled={disabled}
                    onClick={onBrowseClick}
                  >
                    browse
                  </button>
                </span>
                <span className={styles.fileUploadHint}>Max {formatBytes(maxSize)}</span>
              </>
            ) : (
              <div className={styles.fileUploadFileRow}>
                <span className={styles.fileUploadFileName}>{file.name}</span>
                <span className={styles.fileUploadFileSize}>{formatBytes(file.size)}</span>
                <button
                  type="button"
                  className={styles.fileUploadRemoveBtn}
                  disabled={disabled}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove();
                  }}
                  aria-label={`Remove ${file.name}`}
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {error && <p className={styles.fileUploadError}>{error}</p>}
      </div>
    );
  },
);
